import { useState, useEffect } from "react";
import { apiRequest, ApiError } from "src/services/apiClient";
import i18n from "src/i18n";
import { Invoice } from "src/types";
import { InvoiceStatus } from "src/constants/invoice";
import type { ToastState } from "src/components/Toast/Toast";
import { isDemoMode } from "src/demo/demoApi";

type PopupState = {
    title: string;
    message: string;
    action: () => void;
    confirmLabel: string;
    confirmVariant: "primary" | "danger" | "write-off";
    /** Optional middle button, e.g. "save only" next to "save & send". */
    secondary?: { label: string; action: () => void };
};

const DUNNING_LEVEL_KEYS: Record<number, string> = { 1: "friendly", 2: "demanding", 3: "final" };

/** Per-level send mode from Settings → Mahnwesen ("email" | "letter" | "both"). */
export type DunningSendModes = Partial<Record<string, string>>;

export function useInvoiceActions(invoice: Invoice | null, id: string, refetch?: () => void, navigate?: (path: string) => void, dunningSendModes?: DunningSendModes) {
    const [paymentDelta, setPaymentDelta] = useState<number | "">("");
    const [dunning, setDunning] = useState<number>(0);
    const [dunningPaused, setDunningPaused] = useState<boolean>(false);
    const [dueDate, setDueDate] = useState<string>("");
    const [fineRemoved, setFineRemoved] = useState(false);
    const [initialized, setInitialized] = useState(false);
    const [saving, setSaving] = useState(false);
    const [toast, setToast] = useState<ToastState | null>(null);
    const [pdfUrl, setPdfUrl] = useState<string | null>(null);
    const [pdfLoading, setPdfLoading] = useState(false);
    const [pdfFrameLoading, setPdfFrameLoading] = useState(false);
    const [popup, setPopup] = useState<PopupState | null>(null);
    const [portalLinkLoading, setPortalLinkLoading] = useState(false);

    useEffect(() => {
        setInitialized(false);
        setPaymentDelta("");
        setFineRemoved(false);
    }, [id]);

    useEffect(() => {
        if (!invoice || !id || invoice.id !== id) return;
        if (!initialized) {
            setDunning(invoice.dunning ?? 0);
            setDunningPaused(invoice.dunningPaused ?? false);
            setDueDate(invoice.dueDate ? new Date(invoice.dueDate).toISOString().split("T")[0] : "");
            setInitialized(true);
        }
    }, [invoice, id, initialized]);

    const canRecordManualPayment = Boolean(invoice?.sentDate);

    const isDirty =
        initialized &&
        invoice &&
        invoice.id === id &&
        (Number(paymentDelta) > 0 ||
            dunning !== (invoice.dunning ?? 0) ||
            dunningPaused !== (invoice.dunningPaused ?? false) ||
            dueDate !== (invoice.dueDate ? new Date(invoice.dueDate).toISOString().split("T")[0] : "") ||
            fineRemoved);

    const patch = async (fields: Record<string, string | number | boolean | undefined>, successMsg: string): Promise<boolean> => {
        setSaving(true);
        try {
            await apiRequest(
                `/invoices/${id}`,
                { method: "PATCH", body: JSON.stringify(fields) },
                { "Content-Type": "application/json" },
            );
            setToast({ message: successMsg, variant: "success" });
            setPaymentDelta("");
            setFineRemoved(false);
            await refetch?.();
            setInitialized(false);
            return true;
        } catch (e) {
            const msg = e instanceof ApiError ? e.message : i18n.t("common.saveFailed");
            setToast({ message: msg, variant: "error" });
            return false;
        } finally {
            setSaving(false);
        }
    };

    /** "per E-Mail" / "per Brief" / "per E-Mail und Brief" for the level's configured send mode. */
    const channelLabel = (level: number) => {
        const mode = String(dunningSendModes?.[DUNNING_LEVEL_KEYS[level]] ?? "email").trim().toLowerCase();
        return i18n.t(`invoicePage.resendDunningChannel_${mode === "letter" || mode === "both" ? mode : "email"}`);
    };

    const levelLabel = (level: number) => i18n.t(`invoicePage.dunningNotice_${level}`);

    const recipientName = () =>
        [invoice?.recipient?.firstName, invoice?.recipient?.lastName].filter(Boolean).join(" ") || i18n.t("invoicePage.noRecipient");

    /** Sends the patient reminder for the invoice's current (saved) dunning level. */
    const resendDunning = async () => {
        if (isDemoMode()) {
            setToast({ message: i18n.t("common.demoSimulated"), variant: "success" });
            return;
        }
        setSaving(true);
        try {
            const res = await apiRequest<{ emailSent: boolean; letterSent: boolean; emailFailed: boolean; letterFailed: boolean }>(
                `/invoices/${id}/resend-dunning`,
                { method: "POST" },
            );
            const key = res.emailSent && res.letterSent ? "toastDunningResentBoth" : res.letterSent ? "toastDunningResentLetter" : "toastDunningResentEmail";
            const partialFailure = res.emailFailed || res.letterFailed;
            setToast({
                message: i18n.t(partialFailure ? "invoicePage.toastDunningResentPartial" : `invoicePage.${key}`),
                variant: partialFailure ? "error" : "success",
            });
            await refetch?.();
            setInitialized(false);
        } catch (e) {
            const code = e instanceof ApiError ? e.body?.code : undefined;
            setToast({
                message: i18n.t(code === "no_channel" ? "invoicePage.toastDunningResendNoChannel" : "invoicePage.toastDunningResendFailed"),
                variant: "error",
            });
        } finally {
            setSaving(false);
        }
    };

    const handleResendDunning = () => {
        const level = invoice?.dunning ?? 0;
        setPopup({
            title: i18n.t("invoicePage.resendDunningTitle"),
            message: i18n.t("invoicePage.resendDunningMsg", {
                level: levelLabel(level),
                id: invoice?.readableId,
                name: recipientName(),
                channel: channelLabel(level),
            }),
            confirmLabel: i18n.t("invoicePage.resendDunningConfirm"),
            confirmVariant: "primary",
            action: resendDunning,
        });
    };

    const handleSave = () => {
        const todayIso = () => new Date().toISOString().slice(0, 10);

        // If user manually changed dunning from 0 -> non-zero, due date must become "today".
        // Enforce at save-time to guarantee persistence.
        const needsTodayDueDate = (invoice?.dunning ?? 0) === 0 && dunning !== 0;
        const computedDueDate = needsTodayDueDate
            ? todayIso()
            : ((dunning !== 0 && !dueDate && !invoice?.dueDate) ? todayIso() : dueDate);

        if (computedDueDate && computedDueDate !== dueDate) {
            // Keep UI in sync immediately; persistence happens via PATCH below.
            setDueDate(computedDueDate);
        }

        const fields: Record<string, string | number | boolean | undefined> = {
            dunning,
            dunningPaused,
            // If user manually sets a dunning level (anything but 0),
            // ensure the invoice has a due date. Default to "today"
            // if none is currently set.
            dueDate: (computedDueDate || undefined),
        };
        if (Number(paymentDelta) > 0) {
            if (!canRecordManualPayment) {
                setToast({ message: i18n.t("invoicePage.paymentRequiresSent"), variant: "error" });
                return;
            }
            fields.paymentDelta = Number(paymentDelta);
        }
        if (fineRemoved) {
            fields.dunningFineAmount = 0;
            // Marks the waive as explicit — otherwise the backend can't tell "waived to
            // zero" apart from "fine was never populated" and would resurrect it on the
            // next read (falls back to recomputing from the dunning level + settings).
            fields.fineWaived = true;
        }

        // Changing the level never sends anything by itself (the cron only sends on its own
        // automatic advance) — so offer to send the new level's reminder right away. Not for
        // Inkasso (no patient notice at that level) or when dunning ends up paused.
        const levelChanged = dunning !== (invoice?.dunning ?? 0);
        const isOpen = invoice?.status === InvoiceStatus.OPEN || invoice?.status === InvoiceStatus.OVERDUE;
        if (levelChanged && dunning >= 1 && dunning <= 3 && !dunningPaused && invoice?.sentDate && isOpen && !(Number(paymentDelta) > 0)) {
            setPopup({
                title: i18n.t("invoicePage.saveDunningSendTitle"),
                message: i18n.t("invoicePage.saveDunningSendMsg", {
                    level: levelLabel(dunning),
                    name: recipientName(),
                    channel: channelLabel(dunning),
                }),
                confirmLabel: i18n.t("invoicePage.saveDunningSendConfirm"),
                confirmVariant: "primary",
                action: async () => {
                    if (await patch(fields, i18n.t("common.changesSaved"))) await resendDunning();
                },
                secondary: {
                    label: i18n.t("invoicePage.saveDunningSaveOnly"),
                    action: () => patch(fields, i18n.t("common.changesSaved")),
                },
            });
            return;
        }

        patch(fields, i18n.t("common.changesSaved"));
    };

    const handleDunningPausedChange = (value: boolean) => {
        setDunningPaused(value);
    };

    const handleMarkPaid = () =>
        setPopup({
            title: i18n.t("invoicePage.markPaidTitle"),
            message: i18n.t("invoicePage.markPaidMsg", { id: invoice?.readableId }),
            confirmLabel: i18n.t("invoicePage.markPaidConfirm"),
            confirmVariant: "primary",
            action: async () => {
                if (!invoice?.sentDate) {
                    setToast({ message: i18n.t("invoicePage.paymentRequiresSent"), variant: "error" });
                    return;
                }
                // Same net → gross → totalBase formula as InvoiceAmountsCard's "remaining" —
                // paidAmount must cover material costs, VAT, and any accrued dunning fine, not
                // just the base fee, or the invoice still shows a remaining balance after this.
                const fee          = invoice?.amount ?? 0;
                const materialCosts = invoice?.materialCosts ?? 0;
                const net          = +(fee + materialCosts).toFixed(2);
                const vatRate      = invoice?.vatRate ?? 0;
                const vatAmount    = vatRate > 0 ? +(net * vatRate / 100).toFixed(2) : 0;
                const gross        = +(net + vatAmount).toFixed(2);
                const fine         = invoice?.dunningFineAmount ?? 0;
                const totalBase    = +(gross + fine).toFixed(2);
                await patch({ paidAmount: totalBase, status: InvoiceStatus.PAID }, i18n.t("invoicePage.toastMarkedPaid"));
            },
        });

    /** Manual (re)send of the payment receipt — the automatic one is never retried after a failure. */
    const handleResendPaymentReceipt = () =>
        setPopup({
            title: i18n.t(invoice?.paymentConfirmationSentAt ? "invoicePage.paymentReceiptResend" : "invoicePage.paymentReceiptSend"),
            message: i18n.t("invoicePage.paymentReceiptMsg", { id: invoice?.readableId, email: invoice?.recipient?.email }),
            confirmLabel: i18n.t("invoicePage.paymentReceiptSend"),
            confirmVariant: "primary",
            action: async () => {
                if (isDemoMode()) {
                    setToast({ message: i18n.t("common.demoSimulated"), variant: "success" });
                    return;
                }
                setSaving(true);
                try {
                    await apiRequest(`/invoices/${id}/resend-payment-receipt`, { method: "POST" });
                    setToast({ message: i18n.t("invoicePage.toastPaymentReceiptSent"), variant: "success" });
                } catch {
                    setToast({ message: i18n.t("invoicePage.toastPaymentReceiptFailed"), variant: "error" });
                } finally {
                    setSaving(false);
                    await refetch?.();
                }
            },
        });

    const handleWriteOff = () =>
        setPopup({
            title: i18n.t("invoicePage.writeOffTitle"),
            message: i18n.t("invoicePage.writeOffMsg", { id: invoice?.readableId }),
            confirmLabel: i18n.t("invoicePage.writeOffConfirm"),
            confirmVariant: "write-off",
            action: () => patch({ status: InvoiceStatus.WRITTEN_OFF }, i18n.t("invoicePage.toastWrittenOff")),
        });

    const handleReopen = () =>
        setPopup({
            title: i18n.t("invoicePage.reopenTitle"),
            message: i18n.t("invoicePage.reopenMsg", { id: invoice?.readableId }),
            confirmLabel: i18n.t("invoicePage.reopenConfirm"),
            confirmVariant: "primary",
            action: () => patch({ status: InvoiceStatus.OPEN }, i18n.t("invoicePage.toastReopened")),
        });

    const handleDelete = () =>
        setPopup({
            title: i18n.t("invoicePage.deleteInvoice"),
            message: i18n.t("invoicePage.deleteMsg", { id: invoice?.readableId }),
            confirmLabel: i18n.t("invoicePage.deleteInvoiceBtn"),
            confirmVariant: "danger",
            action: async () => {
                try {
                    await apiRequest(`/invoices/${id}`, { method: "DELETE" });
                    navigate?.("/invoices");
                } catch {
                    setToast({ message: i18n.t("invoices.toastDeleteFailed"), variant: "error" });
                }
            },
        });

    const handleSendEmail = () => {
        const alreadySent = invoice?.sentDate
            ? i18n.t("invoicePage.sendEmailAlreadySent", { date: new Date(invoice.sentDate).toLocaleDateString() })
            : "";
        const recipientName = [invoice?.recipient?.firstName, invoice?.recipient?.lastName]
            .filter(Boolean).join(" ");
        setPopup({
            title: i18n.t("invoicePage.sendEmailTitle"),
            message: `${alreadySent}${i18n.t("invoicePage.sendEmailMsg", { id: invoice?.readableId, name: recipientName || i18n.t("invoicePage.noRecipient") })}`,
            confirmLabel: i18n.t("invoicePage.sendEmailConfirm"),
            confirmVariant: "primary",
            action: async () => {
                try {
                    if (isDemoMode()) {
                        setToast({ message: i18n.t("common.demoSimulated"), variant: "success" });
                        return;
                    }
                    await apiRequest(`/notifications/send`, { method: "POST", body: JSON.stringify({ invoiceId: id }) }, { "Content-Type": "application/json" });
                    setToast({ message: i18n.t("invoicePage.toastEmailSent"), variant: "success" });
                } catch {
                    setToast({ message: i18n.t("invoicePage.toastEmailSendFailed"), variant: "error" });
                }
            },
        });
    };

    const handleViewPdf = async () => {
        if (isDemoMode()) {
            setToast({ message: i18n.t("common.demoSimulated"), variant: "success" });
            return;
        }
        setPdfLoading(true);
        try {
            setPdfFrameLoading(true);
            const token    = localStorage.getItem("token");
            const praxisId = JSON.parse(localStorage.getItem("praxis") || "null")?.id;
            const doctorId = JSON.parse(localStorage.getItem("doctor") || "null")?.id;
            const { config } = await import("configs/config");
            // Fetch PDF directly as a blob so the iframe uses a blob: URL,
            // which is never blocked by X-Frame-Options or CSP frame-ancestors.
            // The backend sends Cache-Control: private, max-age=300 on a cache hit (so a
            // second view of an unchanged invoice doesn't re-render the PDF) — without
            // cache: "no-cache" here, the browser's own HTTP cache can silently serve that
            // stale blob for up to 5 minutes on this exact URL, even after editing the
            // patient/recipient bumps the server-side cache key. "no-cache" (despite the
            // name) doesn't disable caching — it forces revalidation with the server via
            // If-None-Match on every call, so a real content change (new ETag) always comes
            // through immediately while an unchanged one still gets a cheap 304.
            const res = await fetch(`${config.baseUrl}/api/invoices/${id}/pdf`, {
                cache: "no-cache",
                headers: {
                    ...(token    && { Authorization: `Bearer ${token}` }),
                    ...(doctorId && { "X-Doctor-Id": doctorId }),
                    ...(praxisId && { "X-Praxis-Id": praxisId }),
                },
            });
            if (!res.ok) throw new Error("Failed to generate PDF");
            const blob = await res.blob();
            setPdfUrl(URL.createObjectURL(blob));
        } catch {
            setToast({ message: i18n.t("invoicePage.toastPdfFailed"), variant: "error" });
            setPdfFrameLoading(false);
        } finally {
            setPdfLoading(false);
        }
    };

    const closePdf = () => {
        if (pdfUrl?.startsWith("blob:")) URL.revokeObjectURL(pdfUrl);
        setPdfUrl(null);
        setPdfFrameLoading(false);
    };

    const handleSendPortalLink = async () => {
        if (!id) return;
        if (isDemoMode()) {
            setToast({ message: i18n.t("common.demoSimulated"), variant: "success" });
            return;
        }
        setPortalLinkLoading(true);
        try {
            await apiRequest(
                "/patient-portal/create-link",
                { method: "POST", body: JSON.stringify({ invoiceId: id }) },
                { "Content-Type": "application/json" },
            );
            setToast({ message: i18n.t("invoicePage.toastPortalLinkSent"), variant: "success" });
            setInitialized(false);
            refetch?.();
        } catch (e: unknown) {
            const msg = e instanceof Error ? e.message : "";
            setToast({ message: msg === "LEGAL_NOT_ACCEPTED" ? i18n.t("invoicePage.legalNotAccepted") : (msg || i18n.t("invoicePage.toastPortalLinkSendFailed")), variant: "error" });
        } finally {
            setPortalLinkLoading(false);
        }
    };

    const [lxStatusJob, setLxStatusJob] = useState<Record<string, any> | null>(null);

    const handleCheckLetterStatus = async () => {
        if (!id) return;
        try {
            const data = await apiRequest<any>(`/notifications/letterxpress-status/${invoice?.letterXpressId}`, {}, {});
            const job = data?.data ?? null;
            if (!job) setToast({ message: i18n.t("invoicePage.toastLxJobNotFound"), variant: "error" });
            setLxStatusJob(job);
        } catch (e: unknown) {
            setToast({ message: e instanceof Error ? e.message : i18n.t("invoicePage.toastLxStatusFailed"), variant: "error" });
        }
    };

    /** `resend`: a second letter of an invoice already posted (backend only allows it when asked). */
    const handleSendViaLetterXpress = async (color: boolean, duplex: boolean, envelope: string, dispatch: string, shipping: string, resend = false) => {
        if (!id) return;
        try {
            if (isDemoMode()) {
                setToast({ message: i18n.t("common.demoSimulated"), variant: "success" });
                return;
            }
            await apiRequest(
                "/notifications/send-letterxpress",
                { method: "POST", body: JSON.stringify({ invoiceId: id, color, duplex, envelope, dispatch, shipping, ...(resend ? { resend: true } : {}) }) },
                { "Content-Type": "application/json" },
            );
            setToast({ message: i18n.t(resend ? "invoicePage.toastLxResent" : "invoicePage.toastLxSentSingle"), variant: "success" });
            setInitialized(false);
            refetch?.();
        } catch (e: unknown) {
            setToast({ message: e instanceof Error ? e.message : i18n.t("invoicePage.toastLxSendFailed"), variant: "error" });
        }
    };

    return {
        canRecordManualPayment,
        paymentDelta, setPaymentDelta,
        dunning, setDunning,
        dunningPaused, setDunningPaused,
        fineRemoved, setFineRemoved,
        dueDate, setDueDate,
        isDirty, saving,
        toast, setToast,
        pdfUrl, pdfLoading, pdfFrameLoading, setPdfFrameLoading, closePdf,
        popup, setPopup,
        portalLinkLoading,
        handleSave, handleResendDunning, handleDunningPausedChange, handleMarkPaid, handleResendPaymentReceipt, handleWriteOff, handleReopen, handleDelete, handleSendEmail, handleViewPdf, handleSendPortalLink, handleSendViaLetterXpress, handleCheckLetterStatus, lxStatusJob, setLxStatusJob,
    };
}
